//START GENAI@CHATGPT4
import { z } from 'zod';
import { isIframe, isWindow } from 'common/type-guards';
import { ConsumerIdentityModel } from './consumer-identity';
//START GENAI@CHATGPT4
const VerificationTypeModel = z.enum(['CARDHOLDER']).optional();
//START GENAI@CHATGPT4
const VerificationDataModel = z.object({
  methodResults: z.record(z.any()).optional(),
  verificationEntity: z.string().length(2).optional(),
  verificationEvents: z.array(z.string().length(2)).optional(),
  verificationMethod: z.string().length(2).optional(),
  verificationResults: z.string().length(2).optional(),
  verificationTimestamp: z.string().optional(),
  verificationType: VerificationTypeModel.optional()
});

const EciModel = z.enum(['05', '06', '07']);

const AssuranceDataModel = z.object({
  eci: EciModel.optional(),
  verificationData: z.array(VerificationDataModel).optional()
});

const AuthenticationSubjectModel = z.enum(['CARD', 'CARDHOLDER', 'CONSUMER']).optional();
const UriTypeModel = z.enum(['APP_URI', 'WEB_URI']).optional();

const UriDataModel = z
  .object({
    uri: z.string().max(2048).optional(),
    uriType: UriTypeModel.optional()
  })
  .optional();

//START GENAI@GHCOPILOT
//START GENAI@GHCOPILOT
const AuthenticationMethodTypeModel = z.enum([
  'CSC_VALIDATION',
  'SMS_OTP',
  'EMAIL_OTP',
  'APP_AUTHENTICATION',
  '3DS',
  'FIDO2'
]);

const AuthenticationMethodModel = z.object({
  authenticationCredentialReference: z.string().max(255).optional(),
  authenticationMethodType: AuthenticationMethodTypeModel,
  authenticationSubject: AuthenticationSubjectModel,
  methodAttributes: z.record(z.any()).optional(),
  uriData: UriDataModel.optional()
});
//END GENAI@GHCOPILOT

const AuthenticationReasonModel = z.enum(['TRANSACTION_AUTHENTICATION']).optional();
//END GENAI@GHCOPILOT

const ComplianceTypeModel = z
  .enum(['PRIVACY_POLICY', 'REMEMBER_ME', 'TERMS_AND_CONDITIONS'])
  .optional();

const ComplianceResourceModel = z
  .object({
    complianceType: ComplianceTypeModel,
    datePublished: z.string().optional(),
    uri: z.string().max(1024),
    version: z.string().max(10).optional()
  })
  .optional();

const ComplianceSettingsModel = z
  .object({
    complianceResources: z.array(ComplianceResourceModel).optional()
  })
  .optional();
//START GENAI@CHATGPT4

const TransactionAmount = z.object({
  //START GENAI@GHCOPILOT
  transactionAmount: z.union([
    z
      .string()
      .max(18)
      .refine(val => !isNaN(parseFloat(val)), {
        message: 'transactionAmount must be a valid number'
      }),
    z.number().refine(val => val.toString().length <= 18, {
      message: 'The transactionAmount cannot exceed 18 characters, including the decimal point'
    })
  ]),
  //END GENAI@CHATGPT4
  transactionCurrencyCode: z.string().length(3)
});
//END GENAI@CHATGPT4

const AddressPreference = z.enum(['NONE', 'FULL', 'POSTAL_COUNTRY']).optional();

const DynamicDataType = z
  .enum([
    'CARD_APPLICATION_CRYPTOGRAM_LONG_FORM',
    'DYNAMIC_CARD_SECURITY_CODE',
    'NONE',
    'CARD_SECURITY_CODE',
    'CARDHOLDER_AUTHENTICATION_CRYPTOGRAM',
    'CARD_APPLICATION_CRYPTOGRAM_SHORT_FORM'
  ])
  .optional();

const PaymentOptions = z.object({
  dynamicDataType: DynamicDataType.optional(),
  dpaDynamicDataTtlMinutes: z.union([z.string(), z.number()]).optional()
});

const PayloadRequested = z.enum(['AUTHENTICATED', 'NON_AUTHENTICATED']).optional();

//START GENAI@GHCOPILOT
const AuthenticationPreferences = z.object({
  authenticationMethods: AuthenticationMethodModel.array().optional(),
  payloadRequested: PayloadRequested.optional(),
  supressChallenge: z.boolean().optional()
});
//END GENAI@GHCOPILOT

const RecurringData = z.object({
  recurringAmount: z.string().max(48).optional(),
  recurringCurrency: z.string().length(3).optional(),
  recurringDate: z.string().length(8).optional(),
  recurringExpiry: z.string().length(8).optional(),
  recurringExponent: z.string().length(1).optional(),
  recurringFrequency: z.string().max(4).optional(),
  recurringInd: z.object({}).optional()
});

export const DpaTransactionOptionsModel = z.object({
  acquirerBIN: z.string().max(11).optional(),
  acquirerMerchantId: z.string().max(35).optional(),
  authenticationPreferences: AuthenticationPreferences.optional(),
  confirmPayment: z.boolean().optional(),
  consumerNationalIdentifierRequested: z.boolean().optional(),
  dpaAcceptedBillingCountries: z.array(z.string()).optional(),
  dpaAcceptedShippingCountries: z.array(z.string()).optional(),
  dpaBillingPreference: AddressPreference.optional(),
  dpaLocale: z.string().optional(),
  dpaShippingPreference: AddressPreference.optional(),
  merchantCategoryCode: z.string().length(4).optional(),
  merchantCountryCode: z.string().length(2).optional(),
  merchantName: z.string().max(40).optional(),
  merchantOrderId: z.string().max(255).optional(),
  //START GENAI@GHCOPILOT
  //END GENAI@GHCOPILOT
  paymentOptions: z.array(PaymentOptions).optional(),
  recurringData: RecurringData.optional(),
  transactionAmount: TransactionAmount.optional()
});

const Address = z.object({
  addressId: z.string().uuid().optional(),
  city: z.string().max(50).optional(),
  countryCode: z.string().length(2).optional(),
  createTime: z.string().optional(),
  lastUsedTime: z.string().optional(),
  line1: z.string().max(75).optional(),
  line2: z.string().max(75).optional(),
  line3: z.string().max(75).optional(),
  name: z.string().max(100).optional(),
  state: z.string().max(30).optional(),
  zip: z.string().max(16).optional()
});

//eslint-disable-next-line
const CardModel = z.object({
  billingAddress: Address.optional(),
  cardholderFirstName: z.string().max(50).optional(),
  cardholderFullName: z.string().max(100).optional(),
  cardholderLastName: z.string().max(50).optional(),
  cardSecurityCode: z.string().length(3).or(z.string().length(4)).optional(),
  panExpirationMonth: z.string().length(2).optional(),
  panExpirationYear: z.string().length(4).optional(),
  paymentAccountReference: z.string().max(29).optional(),
  primaryAccountNumber: z.string().min(9).max(19).optional()
});

//START GENAI@CHATGPT4
const ISO3166Alpha2 = z.string().length(2, 'Must be a valid ISO 3166-1 alpha 2 country code');
const EmailAddress = z
  .string()
  .email('Must be a valid email address')
  .max(255, 'Email address must be at most 255 characters');

const PhoneNumber = z.object({
  countryCode: z
    .string()
    .min(1, 'Country code must be at least 1 character')
    .max(4, 'Country code must be at most 4 characters'),
  phoneNumber: z
    .string()
    .min(4, 'Phone number must be at least 4 characters')
    .max(14, 'Phone number must be at most 14 characters')
});

const ConsumerModel = z.object({
  consumerIdentity: ConsumerIdentityModel,
  countryCode: ISO3166Alpha2.optional(),
  emailAddress: EmailAddress.optional(),
  firstName: z.string().max(50, 'First name must be at most 50 characters').optional(),
  fullName: z.string().max(100, 'Full name must be at most 100 characters').optional(),
  lastName: z.string().max(50, 'Last name must be at most 50 characters').optional(),
  locale: ISO3166Alpha2.optional(),
  mobileNumber: PhoneNumber.optional(),
  nationalIdentifier: z
    .string()
    .max(20, 'National identifier must be at most 20 characters')
    .optional()
});

export const CheckoutInputDataModelSelectCard = z
  .object({
    assuranceData: AssuranceDataModel.optional(),
    authenticationMethod: AuthenticationMethodModel.optional(),
    authenticationReasons: z.array(AuthenticationReasonModel).optional(),
    complianceSettings: ComplianceSettingsModel.optional(),
    consumer: ConsumerModel.optional(),
    dpaTransactionOptions: DpaTransactionOptionsModel.optional(),
    encryptedCard: z.string().optional(),
    payloadTypeIndicatorCheckout: z.string().optional(),
    //START GENAI@CHATGPT4
    srcDigitalCardId: z.string().optional(),
    vsbFlow: z.boolean().optional(),
    windowRef: z
      .preprocess(val => (isIframe(val) ? val.contentWindow : val || undefined), z.any())
      .refine(val => !val || isWindow(val), {
        message: '"windowRef" must be a Window or HTMLIframeElement'
      })
      .optional()
  })
  //END GENAI@CHATGPT4
  .refine(
    data => {
      return data.srcDigitalCardId?.trim() !== '' || data.encryptedCard?.trim() !== '';
    },
    {
      message: 'Either "encryptedCard" or "srcDigitalCardId" must be present',
      path: ['encryptedCard', 'srcDigitalCardId']
    }
  )
  .refine(
    data => {
      return !data.encryptedCard || (data.encryptedCard && data.consumer?.mobileNumber);
    }
    //END GENAI@CHATGPT4
  );
//END GENAI@CHATGPT4
//END GENAI@CHATGPT4
//END GENAI@CHATGPT4
